import type { UnifiedCheckoutBasketType } from "@sellar-io/api-v2/types";
import { useEvents } from "../../../../hooks";
import { useMarketStorefrontSessionId } from "../../../../hooks/useMarketStorefrontSessionTracking";
import type { ConsolidatedOrderingJourneySource } from "../../../../hooks/useMarketStorefrontSessionTracking";
import type { ConsolidatedOrderingBasketSnapshot } from "../../../../hooks/useConsolidatedOrderingIntercom";

export type ConsolidatedBasketOpenSource =
  | "navbar"
  | "flyover"
  | "toast"
  | "other";

export const getConsolidatedBasketProperties = (
  basket: UnifiedCheckoutBasketType,
  session: {
    sessionId: string | null;
    journeySource: ConsolidatedOrderingJourneySource;
  },
) => {
  const supplierCount = basket.DraftOrders.length;
  const skuCount = basket.DraftOrders.reduce(
    (count, order) => count + order.Items.length,
    0,
  );

  return {
    ...(session.sessionId ? { sessionId: session.sessionId } : {}),
    journeySource: session.journeySource,
    sdBasketUuid: basket.uuid,
    supplierCount,
    skuCount,
    isConsolidatedBasket: supplierCount >= 2,
  };
};

export const useConsolidatedBasketTracking = () => {
  const { track } = useEvents();
  const session = useMarketStorefrontSessionId();

  const trackBasketOpened = (
    baskets: UnifiedCheckoutBasketType[],
    source: ConsolidatedBasketOpenSource,
  ) => {
    for (const basket of baskets) {
      track("Delivered By Sellar - Basket Opened", {
        ...getConsolidatedBasketProperties(basket, session),
        source,
      });
    }
  };

  return { trackBasketOpened };
};

export const getConsolidatedBasketIntercomData = (
  basket: UnifiedCheckoutBasketType,
  session: {
    sessionId: string | null;
    journeySource: ConsolidatedOrderingJourneySource;
  },
): {
  snapshot: ConsolidatedOrderingBasketSnapshot;
  metadata: Record<string, string | number>;
} => {
  const items = basket.DraftOrders.flatMap((order) => order.Items);
  const itemCount = items.reduce((count, item) => count + item.quantity, 0);
  const earliestOrderCutoffAt = items
    .map((item) => item.orderCutoffAt)
    .filter((value): value is Date => value != null)
    .map((value) => new Date(value))
    .sort((first, second) => first.getTime() - second.getTime())[0];

  return {
    snapshot: {
      ...(session.sessionId ? { sessionId: session.sessionId } : {}),
      journeySource: session.journeySource,
      supplierCount: basket.DraftOrders.length,
      itemCount,
      paidSupplierCount: basket.Orders.length,
      failedSupplierCount: 0,
    },
    metadata: {
      weekendBasketItemCount: basket.window === "weekend" ? itemCount : 0,
      nextWeekBasketItemCount: basket.window === "nextWeek" ? itemCount : 0,
      ...(earliestOrderCutoffAt
        ? { earliestOrderCutoffAt: earliestOrderCutoffAt.toISOString() }
        : {}),
      ...(basket.requestedDeliveryAt
        ? { deliveryDate: new Date(basket.requestedDeliveryAt).toISOString() }
        : {}),
    },
  };
};
